interface IndexingProgressProps {
  total: number;
  indexed: number;
  failed: number;
  status: string;
  failedDetails?: Array<{ filename: string; reason: string; message: string }>;
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Preparing...';
    case 'fetching':
      return 'Fetching documents...';
    case 'indexing':
      return 'Indexing documents...';
    case 'ready':
      return 'Ready';
    case 'error':
      return 'Error';
    case 'unsupported':
      return 'Not Supported';
    default:
      return status;
  }
}

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'ready':
      return 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400';
    case 'error':
      return 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400';
    case 'fetching':
    case 'indexing':
      return 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-400';
    case 'unsupported':
      return 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  }
}

export default function IndexingProgress({ total, indexed, failed, status, failedDetails }: IndexingProgressProps) {
  const isActive = status === 'fetching' || status === 'indexing' || status === 'pending';
  const pct = total > 0 ? Math.min(100, Math.round((indexed / total) * 100)) : 0;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-6 py-4 dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {indexed - failed} of {total} documents indexed
          {failed > 0 && (
            <span className="ml-2 text-error-600 dark:text-error-400">
              ({failed} failed)
            </span>
          )}
        </p>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClass(status)}`}>
          {isActive && (
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
          )}
          {getStatusLabel(status)}
        </span>
      </div>

      <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div
          className={`h-full rounded-full bg-brand-500 transition-all duration-500 ${isActive ? 'animate-pulse' : ''}`}
          style={{ width: `${status === 'ready' ? 100 : pct}%` }}
        />
      </div>

      {total > 0 && (
        <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500 text-right">
          {status === 'ready' ? '100%' : `${pct}%`}
        </p>
      )}

      {failedDetails && failedDetails.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-amber-600 dark:text-amber-400">
          {failedDetails.map((d) => (
            <li key={d.filename}>
              {d.message}: <span className="font-medium break-all">{d.filename}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
